import type { JSX } from 'react';
import { Chip, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { AsyncSection } from '../components/AsyncSection.js';
import { DataTable } from '../components/DataTable.js';

interface JobRow {
  id: string;
  kind: string;
  status: string;
  attempts: number;
  maxAttempts: number;
  errorCode: string | null;
  errorMessage: string | null;
  leaseOwner: string | null;
  leaseExpiresAt: string | null;
  circleId: string | null;
  createdAt: string;
  finishedAt: string | null;
}

export function JobsPage(): JSX.Element {
  const query = useQuery({
    queryKey: ['admin', 'jobs'],
    queryFn: () =>
      api.get<{ items: JobRow[]; outbox: Array<{ status: string; count: number }> }>(
        '/v1/admin/jobs?limit=50',
      ),
    refetchInterval: 15_000,
  });

  return (
    <>
      <Typography variant="h1" gutterBottom>
        Jobs e outbox
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Reprocessamento é seguro porque todo handler é idempotente. O painel mostra o erro
        sanitizado: nunca stack trace, nunca dado pessoal.
      </Typography>
      <AsyncSection query={query} emptyMessage="Nenhum job registrado.">
        {(data) => (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              {data.outbox.map((row) => (
                <Chip key={row.status} label={`outbox ${row.status}: ${row.count}`} variant="outlined" />
              ))}
            </Stack>
            <DataTable
              rows={data.items}
              getRowKey={(row) => row.id}
              emptyMessage="Nenhum job registrado."
              columns={[
                { key: 'kind', label: 'Tipo', render: (row) => row.kind },
                {
                  key: 'status',
                  label: 'Estado',
                  render: (row) => (
                    <Chip
                      size="small"
                      label={row.status}
                      color={
                        row.status === 'succeeded'
                          ? 'success'
                          : row.status === 'failed'
                            ? 'error'
                            : row.status === 'processing'
                              ? 'info'
                              : 'default'
                      }
                    />
                  ),
                },
                {
                  key: 'attempts',
                  label: 'Tentativas',
                  align: 'right',
                  render: (row) => `${row.attempts}/${row.maxAttempts}`,
                },
                {
                  key: 'error',
                  label: 'Erro',
                  render: (row) =>
                    row.errorCode ? `${row.errorCode}: ${row.errorMessage ?? ''}` : '—',
                },
                { key: 'lease', label: 'Lease', render: (row) => row.leaseOwner ?? '—' },
                {
                  key: 'created',
                  label: 'Criado',
                  render: (row) => new Date(row.createdAt).toLocaleString('pt-BR'),
                },
              ]}
            />
          </>
        )}
      </AsyncSection>
    </>
  );
}